import React, { useEffect, useMemo, useState } from 'react'
import { Card, Select } from '@ui'
import { listAuditEvents, listOrgs } from '../api.js'
import { ActionBadge } from '../components/ActionBadge.jsx'

export function AuditLog() {
  const [entries, setEntries] = useState(null)
  const [orgs, setOrgs] = useState([])
  const [filter, setFilter] = useState('')
  const [err, setErr] = useState(null)

  useEffect(() => {
    listOrgs().then(setOrgs).catch(() => setOrgs([]))
  }, [])

  useEffect(() => {
    setErr(null)
    setEntries(null)
    listAuditEvents(filter || null)
      .then(setEntries)
      .catch((e) => setErr(e.message || 'Failed to load audit events.'))
  }, [filter])

  const orgNames = useMemo(() => {
    const m = new Map()
    for (const t of orgs) m.set(t.orgId, t.name)
    return m
  }, [orgs])

  return (
    <>
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>Audit log</h2>
          <p style={styles.sub}>
            {entries === null ? 'Loading…' : `${entries.length} recent events`}
          </p>
        </div>
        <div style={{ width: 280 }}>
          <Select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            aria-label="Filter by org"
          >
            <option value="">All orgs</option>
            {orgs.map((t) => (
              <option key={t.orgId} value={t.orgId}>{t.name}</option>
            ))}
          </Select>
        </div>
      </div>

      <Card style={{ padding: 16 }}>
        {err && <p style={{ color: 'var(--danger-text)' }}>{err}</p>}
        {entries === null ? (
          !err && <p>Loading…</p>
        ) : entries.length === 0 ? (
          <p style={{ opacity: 0.7 }}>No audit events.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--border)' }}>
                <th style={th}>Time</th>
                <th style={th}>Action</th>
                <th style={th}>Org</th>
                <th style={th}>Detail</th>
                <th style={th}>Actor</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={`${e.ts}-${e.action}`} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ ...td, fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>
                    {new Date(e.ts).toLocaleString()}
                  </td>
                  <td style={td}><ActionBadge action={e.action} /></td>
                  <td style={td}>{orgNames.get(e.orgId) || e.orgId}</td>
                  <td style={td}>{e.detail}</td>
                  <td style={{ ...td, opacity: 0.7 }}>{e.actor}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </>
  )
}

const styles = {
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 20,
    gap: 16,
  },
  title: { fontSize: 20, fontWeight: 600, marginBottom: 2 },
  sub: { fontSize: 12, color: 'var(--text-secondary)' },
}
const th = { padding: '8px 12px', fontWeight: 600, fontSize: 12, opacity: 0.7 }
const td = { padding: '10px 12px' }
